import { faSearch } from "@fortawesome/free-solid-svg-icons/faSearch";
import { faTimesCircle } from "@fortawesome/free-solid-svg-icons/faTimesCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { PagedMessageResponse } from "app/api/types/PagedMessageResponse";
import { Paginacao } from "app/components/itens-orcamento/paginacao/Paginacao";
import { IColuna } from "app/types/IColuna";
import clsx from "clsx";
import globalMU from "globalMU";
import * as R from "ramda";
import React, { memo, useCallback, useMemo, useState } from "react";
import { TableHeadPesquisaPreVendas } from "./TableHead/TableHeadPesquisaPreVendas";
const estiloPesquisaPreVendas =
  require("./pesquisa-pre-vendas.module.scss").default;
const estilosTabelas = require("../../../estilos/tabelas.module.scss").default;

export type PreVenda = {
  Id: number;
  Descricao: string;
  CodigoVendedor: string;
  DataCriacao: string;
  NumPed?: any;
  QuantidadeItens: number;
  ItensPreVenda: ItemPreVenda[];
  UserMessage?: string;
  Transforming: boolean;
};

export type ItemPreVenda = {
  IdPreVenda: number;
  Codigo?: any;
  CodigoProduto: string;
  Quantidade: number;
  TipoEntregaDefinido: string;
  UserMessage?: string;
  Transforming: boolean;
};

export type PesquisaPreVendasProps = {
  campoBusca: string;
  preVendas: PagedMessageResponse<PreVenda>;
  colunas: IColuna[];
  tamanhoFontePadrao: number;
  corFundoSelecionado?: string;
  corTextoSelecionado?: string;
  permitirEngrenagemGrid: boolean
  onPesquisar: () => void;
  onAlterarFonte: (tamanho: number) => void;
  onPreVendasSelecionadas: (preVendaSelecionada: PreVenda[]) => void;
  onToggleSelecionarTodasColunas: (selecionar: boolean) => void;
  onToggleColunaSelecionada: (nomeColuna: string, selecionada: boolean) => void;
  onAlterarListaSelecionada: (numeroIndexPagina: number) => void;
  onAlterarCampoLocalizar: (localizar: string) => void;
  onLimparCampoLocalizar: () => void;
  onMensagemError: () => void;
};

// Mapa fixo — elevado ao escopo do módulo para não ser recriado a cada chamada de
// traduzirNomeColuna (item #27), que antes rodava até 5x por LINHA da grade.
const _MAPA_COLUNAS_PRE_VENDA: Record<string, string> = {
  Id: "Nº Pré-venda",
  Descricao: "Descrição",
  DataCriacao: "Data/Hora",
  CodigoVendedor: "Vendedor",
  ItensPreVenda: "Quantidade Itens",
};

const traduzirNomeColuna = (nomeColuna: string) =>
  nomeColuna in _MAPA_COLUNAS_PRE_VENDA ? _MAPA_COLUNAS_PRE_VENDA[nomeColuna] : nomeColuna;

const PesquisaPreVendas = memo((props: PesquisaPreVendasProps) => {
  const [preVendasSelecionadas, setPreVendasSelecionadas] = useState<PreVenda[]>(
    []
  );

  // deps [] (item #22): setState funcional não precisa de preVendasSelecionadas nas deps —
  // mantém a identidade estável entre renders, então o useMemo das linhas abaixo só recalcula
  // quando a SELEÇÃO em si muda, nunca por causa desta função ter sido recriada.
  const togglePreVendaSelecionada = useCallback((preVendaSelecionada: PreVenda) => {
    setPreVendasSelecionadas((prev) =>
      prev.some((p) => p == preVendaSelecionada)
        ? R.filter((c) => c != preVendaSelecionada, prev)
        : prev.concat(preVendaSelecionada)
    );
  }, []);

  const onClickOk = useCallback(() => {
    preVendasSelecionadas.length > 0
      ? props.onPreVendasSelecionadas(preVendasSelecionadas)
      : props.onMensagemError()
  }, [preVendasSelecionadas, props.onPreVendasSelecionadas, props.onMensagemError]);

  // Renderizado duas vezes (topo e rodapé da grade) — recebe um sufixo para não duplicar id no DOM
  // (mesmo padrão de BarraGrid(sufixoId) em PaginaPesquisaProduto.tsx).
  const botaoOk = (sufixoId: string) =>
    <div
      className={estiloPesquisaPreVendas.form}
      style={{ marginTop: "20px" }}
    >
      <button
        className={estiloPesquisaPreVendas.botao}
        id={`pesquisa-pre-vendas${sufixoId}`}
        onClick={onClickOk}
        disabled={preVendasSelecionadas.length === 0}
      >
        Ok
      </button>
    </div>

  // Quais colunas exibir, calculado UMA VEZ por mudança de `colunas` (não por linha da grade —
  // antes cada uma das 5 colunas fazia um findIndex em props.colunas PARA CADA preVenda renderizada).
  const colunasVisiveis = useMemo(() => {
    const estaSelecionada = (nomeTraduzido: string) =>
      props.colunas.find((coluna) => traduzirNomeColuna(coluna.Nome) === nomeTraduzido)?.Selecionada ?? false

    return {
      nPreVenda: estaSelecionada("Nº Pré-venda"),
      descricao: estaSelecionada("Descrição"),
      dataHora: estaSelecionada("Data/Hora"),
      vendedor: estaSelecionada("Vendedor"),
      quantidadeItens: estaSelecionada("Quantidade Itens"),
    }
  }, [props.colunas]);

  const formLocalizar = <form id="form-pesquisa-pre-vendas" noValidate={true} onSubmit={(e) => { e.preventDefault(); props.onPesquisar(); }}>
      <fieldset className={estiloPesquisaPreVendas.separador}>
        <div className={estiloPesquisaPreVendas.form}>
          <label>Localizar</label>
          <input
            name="Nome"
            id="Nome"
            type="text"
            maxLength={254}
            value={(props.campoBusca) ?? ""}
            onChange={(e) => props.onAlterarCampoLocalizar(e.target.value)}
          />
          <>
            <div>
              <FontAwesomeIcon
                className={estiloPesquisaPreVendas.iconePesquisar}
                icon={faSearch}
                onClick={() => props.onPesquisar()}
              />
            </div>
          </>
          <div className={estiloPesquisaPreVendas.iconeRemover}>
            <FontAwesomeIcon
              icon={faTimesCircle}
              onClick={() => props.onLimparCampoLocalizar()}
            />
          </div>
        </div>
      </fieldset>
    </form>

  // Bloco mais custoso da tela (até TamanhoPagina linhas, 5 colunas condicionais cada) —
  // memoizado para não ser recriado a cada tecla digitada em "Localizar" (props.campoBusca
  // muda a cada tecla e não está nas deps abaixo — item #150/#152).
  const linhasPreVenda = useMemo(() => props.preVendas?.Entities?.map((preVenda) => {
    const selecionada = preVendasSelecionadas.some((p) => p == preVenda)
    return (
      <tr
        key={preVenda.Id}
        id={`PesquisaPreVendas_LinhaTabela_${preVenda.Id}`}
        style={
          selecionada
            ? {
              backgroundColor: props.corFundoSelecionado,
              color: props.corTextoSelecionado,
              fontSize: props.tamanhoFontePadrao,
            }
            : { fontSize: props.tamanhoFontePadrao }
        }
        className={clsx(estilosTabelas.linhaClicavel, selecionada && estilosTabelas.linhaSelecionada)}
        onClick={() => togglePreVendaSelecionada(preVenda)}
      >
        {colunasVisiveis.nPreVenda && <td id={`PesquisaPreVendas_ColunaId_${preVenda.Id}`}>{preVenda.Id}</td>}
        {colunasVisiveis.descricao && <td id={`PesquisaPreVendas_ColunaDescricao_${preVenda.Id}`}>{preVenda.Descricao}</td>}
        {colunasVisiveis.dataHora && <td id={`PesquisaPreVendas_ColunaDataHora_${preVenda.Id}`}>{globalMU.formatDateTime(preVenda.DataCriacao)}</td>}
        {colunasVisiveis.vendedor && <td id={`PesquisaPreVendas_ColunaVendedor_${preVenda.Id}`}>{preVenda.CodigoVendedor}</td>}
        {colunasVisiveis.quantidadeItens && <td id={`PesquisaPreVendas_ColunaQuantidadeItens_${preVenda.Id}`} className="_text-right">{preVenda.QuantidadeItens}</td>}
      </tr>
    );
  }), [
    props.preVendas,
    colunasVisiveis,
    preVendasSelecionadas,
    props.corFundoSelecionado,
    props.corTextoSelecionado,
    props.tamanhoFontePadrao,
    togglePreVendaSelecionada,
  ]);

  const temPreVendas = props.preVendas?.Entities?.length > 0;

  return (
    <>
      {formLocalizar}

      {temPreVendas && (
        <>
          {botaoOk("_Topo")}

          <table id="PesquisaPreVendas_TabelaPreVendas" className={clsx(estilosTabelas.tabela, "_w-full")}>
            <TableHeadPesquisaPreVendas
              permitirEngrenagemGrid={props.permitirEngrenagemGrid}
              onToggleSelecionarTodasColunas={props.onToggleSelecionarTodasColunas}
              onToggleColunaSelecionada={props.onToggleColunaSelecionada}
              colunas={props.colunas}
              onAlterarFonte={props.onAlterarFonte}
              tamanhoFonte={props.tamanhoFontePadrao}
            />

            <tbody id="PesquisaPreVendas_CorpoTabela">
              {linhasPreVenda}
            </tbody>
          </table>

          {props.preVendas?.PageCount > 1 && (
            <Paginacao
              paginaAtual={props.preVendas?.ActualPage}
              totalPaginas={props.preVendas?.PageCount}
              id="PesquisaPreVendas_Paginacao"
              onAlterarListaSelecionada={props.onAlterarListaSelecionada}
            />
          )}

          {botaoOk("_Rodape")}
        </>
      )}
    </>
  );
});

export { PesquisaPreVendas };
